// reset.service — Reset Data (Owner). Aksi paling merusak di aplikasi ini, jadi pagarnya berlapis:
//   1. Backup dijalankan LEBIH DULU (modul Backup yang sudah ada) — bukan sekadar saran, ia gagal
//      maka reset tak berjalan. Salah reset tanpa cadangan = data toko hilang selamanya.
//   2. Password Owner dibuktikan ulang (perangkat yang ditinggal terbuka bukan izin).
//   3. Kode 6 angka dari aplikasi authenticator (2FA) — membuktikan yang menekan tombol memang
//      pemegang alat kedua, bukan orang yang kebetulan duduk di depan layar terbuka. Semula
//      direncanakan lewat email, tapi Supabase mengunci penyuntingan template email di balik SMTP
//      berbayar; 2FA memberi jaminan setara tanpa biaya, dan sudah ada di aplikasi ini.
//   4. Frasa konfirmasi diketik manual — pagar terakhir terhadap salah klik.
//   5. RPC di DB memeriksa sendiri bahwa pemanggilnya Owner.
import { supabase } from '@core/supabase';
import { currentUser } from '@core/session';
import { verifyOwnPassword } from '@modules/auth/auth.service';

export type LingkupReset = 'CABANG' | 'SEMUA';

/** Frasa yang harus diketik persis — memuat sasarannya supaya tak salah cabang. */
export const frasaKonfirmasi = (lingkup: LingkupReset, outlet: string): string =>
  lingkup === 'SEMUA' ? 'HAPUS SEMUA DATA' : `RESET ${outlet.toUpperCase()}`;

/** 2FA sudah aktif? Reset Data mewajibkannya sebagai alat bukti kedua. */
export async function siap2FA(): Promise<boolean> {
  const { activeFactorId } = await import('@modules/auth/mfa.service');
  return !!(await activeFactorId());
}

/** Buktikan kode authenticator. Gagal = reset tak boleh berjalan. */
async function cekKode2FA(kode: string): Promise<void> {
  const { activeFactorId, verifyLoginCode } = await import('@modules/auth/mfa.service');
  const factorId = await activeFactorId();
  if (!factorId) throw new Error('Aktifkan 2FA dulu di 🔑 Keamanan Akun — Reset Data wajib memakai kode authenticator.');
  await verifyLoginCode(factorId, kode);
}

export interface HasilReset { scope: string; outlet: string | null; karyawan_dihapus: number }

export async function resetData(opts: {
  lingkup: LingkupReset; outlet: string; alasan: string; password: string; kode2FA: string; frasa: string;
}): Promise<HasilReset> {
  const u = currentUser(); if (!u) throw new Error('Sesi tidak valid.');
  if (u.role !== 'Owner') throw new Error('Hanya Owner yang boleh mereset data.');
  if (!opts.alasan.trim()) throw new Error('Alasan wajib diisi.');
  if (opts.frasa.trim() !== frasaKonfirmasi(opts.lingkup, opts.outlet)) {
    throw new Error(`Ketik persis: ${frasaKonfirmasi(opts.lingkup, opts.outlet)}`);
  }
  await verifyOwnPassword(opts.password);
  await cekKode2FA(opts.kode2FA);

  // Backup WAJIB berhasil dulu. Diletakkan setelah semua pemeriksaan supaya tak membuat berkas
  // cadangan untuk percobaan yang memang akan ditolak.
  const { runBackup } = await import('@modules/backup/backup.service');
  await runBackup(false, `sebelum-reset-${opts.lingkup === 'SEMUA' ? 'semua' : opts.outlet}`);

  const { data, error } = await supabase().rpc('owner_reset_data', {
    p_scope: opts.lingkup, p_outlet: opts.lingkup === 'SEMUA' ? null : opts.outlet, p_reason: opts.alasan.trim(),
  });
  if (error) throw new Error(error.message);
  return data as HasilReset;
}
